import React, { memo, type ReactElement } from 'react';
import {
  Badge,
  ButtonSelect,
  Icon,
  MenuItem,
  MenuNested,
  type MenuOption,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../hooks';

import './SodSort.css';

interface SodSortProps {
  fields: MenuOption[];
  fieldName: string | undefined;
  actions: MenuOption[];
  actionName: string | undefined;
  handleFieldClick: (fieldValue: string) => void;
  handleActionClick: (actionvalue: string) => void;
  isDisabled?: boolean;
}

/**
 * A component that renders a sorting control for the violations list.
 * Provides a two-level dropdown menu to select sorting field and direction.
 *
 * @example
 * return (
 *   <SodSort />
 * )
 *
 * @returns {JSX.Element} A button component that opens a sorting dropdown menu
 *
 * @remarks
 * The component uses the useGetSortMenu hook to manage sorting state and interactions.
 * It implements a two-level menu where users first select a field to sort by,
 * followed by the sort direction (ascending/descending).
 *
 * The component follows accessibility guidelines:
 * - Provides proper ARIA labels for the dropdown button
 * - Ensures keyboard navigation support
 * - Maintains focus management within the menu
 *
 * @see useGetSortMenu - Custom hook that provides sorting logic
 */
function SodSort({
  fields,
  fieldName,
  actions,
  actionName,
  handleFieldClick,
  handleActionClick,
  isDisabled,
}: SodSortProps): ReactElement {
  const l = useInvertedLocalize();

  return (
    <MenuNested
      placement='bottom-end'
      dropdownClassName='Sod-SortActionMenuDropdown'
      trigger={
        <ButtonSelect
          kind='rectangle'
          color='neutral'
          label={l('Sort')}
          ariaLabel={l('Sort')}
          onClick={() => {}}
          isOpen={false}
          size='small'
          prefixIcon={<Icon kind='Sort' />}
          isDisabled={isDisabled}
          isVisualCueEnabled={false}
          BadgeComp={<Badge kind='dot' color='primary' size='small' />}
        />
      }>
      <MenuNested
        placement='bottom-start'
        tabIndex={-1}
        trigger={
          <ButtonSelect
            kind='rectangle'
            label={fieldName}
            size='small'
            className='Sod-SortActionMenuButton--isField'
            isDisabled={isDisabled}
          />
        }
        shouldCloseMenuOnItemClick={false}
        dropdownClassName='Sod-SortActionMenuDropdown--isField'>
        {fields.map((field) => (
          <MenuItem
            key={field.value}
            option={{
              label: field.label,
              value: field.value,
              isChecked: field.isChecked,
              isDisabled,
            }}
            onClick={() => handleFieldClick(field.value)}
            tabIndex={0}
          />
        ))}
      </MenuNested>
      <MenuNested
        placement='bottom-end'
        tabIndex={-1}
        trigger={
          <ButtonSelect
            kind='rectangle'
            label={actionName}
            size='small'
            className='Sod-SortActionMenuButton--isAction'
            isDisabled={isDisabled}
          />
        }
        dropdownClassName='Sod-SortActionMenuDropdown--isAction'>
        {actions.map((action) => (
          <MenuItem
            key={action.value}
            option={{
              label: action.label,
              value: action.value,
              isChecked: action.isChecked,
              isDisabled,
            }}
            onClick={() => handleActionClick(action.value)}
            tabIndex={0}
          />
        ))}
      </MenuNested>
    </MenuNested>
  );
}

SodSort.defaultProps = {
  isDisabled: false,
};

export default memo(SodSort);
